import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card, { StatCard } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const SearchCompanyDetails = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user, impersonate } = useAuth();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const [detailsRes, employeesRes] = await Promise.all([
        api.get(`/search/company-details/${companyId}`),
        api.get('/search/employees', { params: { companyId } }),
      ]);

      setData(detailsRes.data);
      setEmployees(employeesRes.data);
    } catch (err) {
      console.error(err);
      showToast(t('searchCompanyDetails.loadError'), 'error');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    const fullName = `${emp.user.firstName} ${emp.user.lastName}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    return (
      fullName.includes(search) ||
      emp.user.email.toLowerCase().includes(search) ||
      emp.jobTitle?.toLowerCase().includes(search) ||
      emp.homeAddress?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return <Spinner size="lg" label={t('searchCompanyDetails.loadingDetails')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={data.company.name}
        subtitle={`CUI: ${data.company.cui}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate('/search')}>
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('searchCompanyDetails.backToSearch')}
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label={t('searchCompanyDetails.statEmployees')}
          value={data.stats.totalEmployees}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          }
        />
        <StatCard
          label={t('searchCompanyDetails.statCompleted')}
          value={data.stats.completedTrainings}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
        />
        <StatCard
          label={t('searchCompanyDetails.statInProgress')}
          value={data.stats.inProgressTrainings}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
        />
        <StatCard
          label={t('searchCompanyDetails.statCompletionRate')}
          value={`${data.stats.completionRate}%`}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
        />
      </div>

      {/* Search Employees */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('searchCompanyDetails.employeesTitle')} ({employees.length})
        </h2>

        <div className="relative mb-4">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={t('searchCompanyDetails.employeeSearchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="space-y-3">
          {filteredEmployees.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-center py-8">
              {searchTerm
                ? t('searchCompanyDetails.noEmployeeFound')
                : t('searchCompanyDetails.noEmployees')}
            </p>
          ) : (
            filteredEmployees.map((emp) => (
              <div
                key={emp.id}
                className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 hover:bg-surface-secondary transition"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {emp.user.firstName} {emp.user.lastName}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{emp.user.email}</p>
                    {emp.jobTitle && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                        <svg
                          className="w-4 h-4 text-slate-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        {emp.jobTitle}
                      </p>
                    )}
                    {emp.homeAddress && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        <svg
                          className="w-4 h-4 text-slate-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        {emp.homeAddress}
                      </p>
                    )}
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="flex gap-2 mb-2">
                      <Badge color="green">
                        <svg
                          className="w-3 h-3 inline mr-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {emp.stats.completed}
                      </Badge>
                      <Badge color="yellow">
                        <svg
                          className="w-3 h-3 inline mr-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {emp.stats.inProgress}
                      </Badge>
                      <Badge color="blue">
                        <svg
                          className="w-3 h-3 inline mr-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                        {emp.stats.assigned}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {emp.stats.completed}/{emp.stats.assigned}{' '}
                      {t('searchCompanyDetails.coursesCompleted')}
                    </p>
                    {user?.role === 'SUPER_ADMIN' &&
                      emp.user?.active &&
                      emp.user?.role !== 'SUPER_ADMIN' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 text-amber-700 border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                          title={t('impersonation.buttonTitle')}
                          onClick={async () => {
                            if (
                              !window.confirm(
                                t('impersonation.confirmStart', {
                                  name: `${emp.user.firstName} ${emp.user.lastName}`,
                                })
                              )
                            )
                              return;
                            const res = await impersonate(emp.userId);
                            if (!res.success) showToast(res.error, 'error');
                          }}
                        >
                          {t('impersonation.buttonLabel')}
                        </Button>
                      )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Link to={`/companies/${companyId}/documents`} className="flex-1">
          <Button variant="secondary" size="lg" className="w-full justify-center">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            </svg>
            {t('searchCompanyDetails.documents')}
          </Button>
        </Link>
        <Link to={`/companies/${companyId}/sheets`} className="flex-1">
          <Button variant="accent" size="lg" className="w-full justify-center">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {t('searchCompanyDetails.ssmSheets')}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default SearchCompanyDetails;
